import React from "react";
import { PageContainer } from "@/components/core/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { SkeletonBox } from "@/components/core/skeletons/SkeletonBox";
import { SkeletonText } from "@/components/core/skeletons/SkeletonText";
import { AvatarWithTextSkeleton } from "@/components/core/skeletons/AvatarWithTextSkeleton";
import { FormFieldSkeleton } from "@/components/core/skeletons/FormFieldSkeleton";
import { OrderSkeletonList } from "@/components/core/skeletons/OrderSkeletonList";

interface MemberDetailsSkeletonProps {
  isChargeMember?: boolean;
}

export const MemberDetailsSkeleton = React.memo<MemberDetailsSkeletonProps>(({ 
  isChargeMember = false 
}) => {
  return (
    <PageContainer scrollable className="flex flex-col">
      {/* Title skeleton */}
      <div className="flex items-center gap-4 mb-5">
        <SkeletonBox width="w-6" height="h-6" />
        <SkeletonText size="xl" width="xl" />
      </div>

      {/* Member Profile Section Skeleton */}
      <div>
        {/* Member Profile */}
        <Card className="border-none shadow-none px-0">
          <CardContent className="space-y-6 px-0">
            {/* Avatar + Name section */}
            <AvatarWithTextSkeleton
              avatarSize="xl"
              align="start"
              gap="lg"
              textLines={[
                { size: "lg", width: "2xl" },
                { size: "sm", width: "lg" },
                { size: "sm", width: "xl" },
              ]}
            />
          </CardContent>          
        </Card>

        {/* Member Stats */}
        <Card className="border-none shadow-none pt-0 px-0">
          <CardContent className="px-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <SkeletonText size="sm" width="md" />
                <SkeletonText size="base" width="sm" />
              </div>
              <div className="space-y-2">
                <SkeletonText size="sm" width="sm" />
                <SkeletonText size="base" width="xs" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      {isChargeMember ? (
        /* Charge Member Form Section */
        <div className="flex-1 flex flex-col p-4">
          <div className="space-y-6 flex-1 flex flex-col">
            {/* Amount to Pay Field */}
            <FormFieldSkeleton 
              type="input"
              labelWidth="lg"
            />

            {/* Note Field */}
            <FormFieldSkeleton 
              type="textarea"
              labelWidth="sm"
            />

            {/* Submit Button */}
            <SkeletonBox 
              width="w-full" 
              height="h-10" 
              className="!mt-auto rounded-md"
            />
          </div>
        </div>
      ) : (
        /* Recent Orders Section */
        <Card className="flex-1 pb-0 border-none shadow-none px-0">
          <CardHeader className="px-0">
            <CardTitle>
              <SkeletonText size="lg" width="lg" />
            </CardTitle>
          </CardHeader>

          <CardContent className="h-full px-0">
            <OrderSkeletonList count={3} withAvatar={false} />
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
});

MemberDetailsSkeleton.displayName = "MemberDetailsSkeleton"; 